import { useMemo } from 'react';
import { Clock, Coffee, Flame } from 'lucide-react';
import { DishImage } from '@/components/DishImage';

// Stable seed per day so "Today's Picks" don't reshuffle on every refresh
const dayOfYear = () => {
  const d = new Date();
  const start = new Date(d.getFullYear(), 0, 0);
  const diff = d - start;
  return Math.floor(diff / 86400000);
};

const seededShuffle = (arr, seed) => {
  const a = [...arr];
  let s = seed;
  for (let i = a.length - 1; i > 0; i -= 1) {
    s = (s * 9301 + 49297) % 233280;
    const j = Math.floor((s / 233280) * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
};

const minutesFromPrep = (prepStr) => {
  const m = String(prepStr || '').match(/(\d+)/);
  return m ? parseInt(m[1], 10) : 30;
};

const TIRED_NAMES = new Set([
  'Maggi Noodles', 'Bread Omelet', 'Toast with Butter', 'Bun Butter',
  'Masala Buttermilk', 'Nimbu Paani', 'Egg Bhurji', 'Paneer Bhurji',
  'Curd Rice', 'Maggi Cheese', 'Cheese Maggi', 'Khichdi', 'Poha',
]);

const Card = ({ recipe, onOpen, badge }) => (
  <button
    onClick={() => onOpen(recipe)}
    data-testid={`shelf-card-${recipe.id}`}
    className="group flex-shrink-0 w-[260px] sm:w-[280px] text-left rounded-2xl overflow-hidden bg-white border border-[#1C1917]/5 hover:-translate-y-1 hover:shadow-2xl transition-all duration-300"
  >
    <div className="relative h-40 overflow-hidden">
      <DishImage
        src={recipe.image_url}
        alt={`${recipe.name} recipe`}
        fallbackInitial={recipe.name?.[0] || 'M'}
        className="w-full h-full"
        imgClassName="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
      />
      {badge && (
        <span className="absolute top-3 left-3 px-2.5 py-1 bg-white/85 backdrop-blur rounded-full text-[10px] uppercase tracking-widest font-['Outfit'] font-semibold text-[#F97316]">
          {badge}
        </span>
      )}
    </div>
    <div className="p-4">
      <h3 className="font-['Playfair_Display'] text-lg font-bold text-[#1C1917] leading-snug mb-1.5 line-clamp-2">
        {recipe.name}
      </h3>
      <div className="flex items-center gap-2 text-xs font-['Outfit'] text-[#1C1917]/55">
        <Clock className="w-3 h-3" /> {recipe.prep_time}
        <span className="text-[#1C1917]/20">·</span>
        <span>{recipe.calories} cal</span>
      </div>
    </div>
  </button>
);

const Shelf = ({ icon: Icon, title, subtitle, accent, items, onOpen, badge, testid }) => {
  if (!items?.length) return null;
  return (
    <section className="px-6 py-12" data-testid={testid}>
      <div className="max-w-7xl mx-auto">
        <div className="flex items-end justify-between mb-6">
          <div className="flex items-start gap-3">
            <div
              className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
              style={{ background: `${accent}15` }}
            >
              <Icon className="w-5 h-5" style={{ color: accent }} />
            </div>
            <div>
              <h2 className="font-['Playfair_Display'] text-2xl sm:text-3xl font-bold text-[#1C1917] leading-tight">
                {title}
              </h2>
              <p className="text-sm text-[#1C1917]/55 font-['Outfit'] mt-0.5">{subtitle}</p>
            </div>
          </div>
        </div>
        <div className="flex gap-4 overflow-x-auto pb-3 -mx-6 px-6 snap-x snap-mandatory scrollbar-thin">
          {items.map((r) => (
            <div key={r.id} className="snap-start">
              <Card recipe={r} onOpen={onOpen} badge={badge} />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export const HomeShelves = ({ recipes, onOpen }) => {
  const todaysPicks = useMemo(() => {
    if (!recipes?.length) return [];
    const main = recipes.filter((r) => ['lunch', 'dinner', 'crazy'].includes(r.category));
    return seededShuffle(main, dayOfYear()).slice(0, 8);
  }, [recipes]);

  const quickMeals = useMemo(() => {
    if (!recipes?.length) return [];
    return recipes
      .filter((r) =>
        ['breakfast', 'lunch', 'dinner', 'drinks'].includes(r.category) &&
        minutesFromPrep(r.prep_time) <= 20
      )
      .slice(0, 10);
  }, [recipes]);

  const tiredMeals = useMemo(() => {
    if (!recipes?.length) return [];
    const named = recipes.filter((r) => TIRED_NAMES.has(r.name));
    if (named.length >= 6) return named.slice(0, 8);
    // Fallback: anything ≤15 mins
    return recipes
      .filter((r) => minutesFromPrep(r.prep_time) <= 15)
      .slice(0, 8);
  }, [recipes]);

  return (
    <>
      <Shelf
        icon={Flame}
        title="Today's Picks 🔥"
        subtitle="Fresh suggestions, refreshed daily"
        accent="#F97316"
        items={todaysPicks}
        onOpen={onOpen}
        testid="shelf-todays-picks"
      />
      <Shelf
        icon={Coffee}
        title="Quick Meals · Under 20 min ⚡"
        subtitle="Hungry now? These cook fast."
        accent="#2D4A22"
        items={quickMeals}
        onOpen={onOpen}
        testid="shelf-quick-meals"
      />
    </>
  );
};
